import { createSlice } from '@reduxjs/toolkit'
import { ErrorCode, ConsumerLookupAction } from '@afterpaytouch/portal-api/types'
import {
  authCheckChallenge,
  authCheckCode,
  authReset,
  authResetChallenge,
  checkEmailEligibility,
  closeCheckout,
  emailLookup,
  getCheckout,
  mobileLookup,
} from '../../'
import {
  AuthCheckCodeStatus,
  isPublicResponse,
  PasswordResetTwoFactorChallenge,
  TwoFactorChallengeType,
  VerificationErrorCode,
  VerificationMethod,
} from '../../../model'
import { PURGE } from 'redux-persist'
import { isNullOrUndefined } from '@afterpay/utils'
import { isPortalApiError } from '@afterpaytouch/portal-api'

export enum ChallengeStatus {
  Pending,
  NotRequired,
  Required,
  Checking,
  Verified,
  Error,
}

export interface ChallengeState {
  status: ChallengeStatus
  error?: VerificationErrorCode
  challenges?: PasswordResetTwoFactorChallenge[]
  answers?: Partial<Record<TwoFactorChallengeType, string>>
}

export enum PasswordResetStatus {
  VerificationRequired,
  ChallengeRequired,
  EnterNewPassword,
  ChangingPassword,
  Complete,
  Error,
}

export enum PasswordResetError {
  IdenticalPassword,
  WeakPassword,
  Other,
}

export interface PasswordResetState {
  status: PasswordResetStatus
  error?: PasswordResetError
  code?: string
  verificationMethods: Record<VerificationMethod, boolean>
  autoSendSms: boolean
  initialMethod?: VerificationMethod
  challenge?: ChallengeState
}

export const passwordInitialState: PasswordResetState = {
  status: PasswordResetStatus.VerificationRequired,
  verificationMethods: {
    [VerificationMethod.SMS]: true,
    [VerificationMethod.EMAIL]: false,
  },
  autoSendSms: true,
  challenge: {
    status: ChallengeStatus.Pending,
  },
}

const slice = createSlice({
  name: 'passwordReset',
  initialState: passwordInitialState,
  reducers: {},
  extraReducers: (builder) =>
    builder
      .addCase(authCheckCode.fulfilled, (state, action) => {
        state.code = action.meta.arg.code
        switch (action.payload.status) {
          case AuthCheckCodeStatus.PASS:
            state.status = PasswordResetStatus.EnterNewPassword
            if (isNullOrUndefined(state.challenge)) {
              return
            }
            state.challenge.status = ChallengeStatus.NotRequired
            break
          case AuthCheckCodeStatus.TWO_FACTOR_REQUIRED:
            state.status = PasswordResetStatus.ChallengeRequired
            if (isNullOrUndefined(state.challenge)) {
              return
            }
            state.challenge.status = ChallengeStatus.Required
            state.challenge.challenges = action.payload.challenges
            state.challenge.answers = {}
        }
      })
      .addCase(mobileLookup.fulfilled, (state, action) => {
        if (isPublicResponse(action.payload)) {
          return
        }
        if (action.payload.dateOfBirthVerificationRequired && !isNullOrUndefined(state.challenge)) {
          state.challenge.challenges = [
            {
              type: TwoFactorChallengeType.LAST_NAME,
              hint: '',
            },
            {
              type: TwoFactorChallengeType.DATE_OF_BIRTH,
              hint: '',
            },
          ]
        }
      })
      .addCase(getCheckout.fulfilled, (state, action) => {
        if (action.payload.consumerLookup?.action === ConsumerLookupAction.DATE_OF_BIRTH_VERIFICATION && !isNullOrUndefined(state.challenge)) {
          state.challenge.challenges = [
            {
              type: TwoFactorChallengeType.LAST_NAME,
              hint: '',
            },
            {
              type: TwoFactorChallengeType.DATE_OF_BIRTH,
              hint: '',
            },
          ]
        }
      })
      .addCase(authReset.pending, (state) => {
        state.status = PasswordResetStatus.ChangingPassword
      })
      .addCase(authReset.rejected, (state, action) => {
        state.status = PasswordResetStatus.Error
        state.error = PasswordResetError.Other
        if (action.meta.rejectedWithValue && isPortalApiError(action.payload)) {
          switch (action.payload?.errorCode) {
            case ErrorCode.IdenticalPassword:
              state.error = PasswordResetError.IdenticalPassword
              break
            case ErrorCode.WeakPassword:
              state.error = PasswordResetError.WeakPassword
              break
          }
        }
      })
      .addCase(authReset.fulfilled, (state) => {
        state.status = PasswordResetStatus.Complete
        if (isNullOrUndefined(state.challenge)) {
          return
        }
        state.challenge.status = ChallengeStatus.Pending
      })
      .addCase(authCheckChallenge.pending, (state) => {
        if (isNullOrUndefined(state.challenge)) {
          return
        }
        state.challenge.status = ChallengeStatus.Checking
        state.challenge.error = undefined
      })
      .addCase(authCheckChallenge.rejected, (state, action) => {
        if (isNullOrUndefined(state.challenge)) {
          return
        }

        state.challenge.status = ChallengeStatus.Error
        if (action.meta.rejectedWithValue && isPortalApiError(action.payload)) {
          switch (action.payload?.errorCode) {
            case ErrorCode.PasswordResetTwoFactorChallengesInvalid:
              state.challenge.error = VerificationErrorCode.PasswordResetTwoFactorChallengesInvalid
              break
            case ErrorCode.PasswordResetTwoFactorChallengesMaxAttempts:
              state.challenge.error = VerificationErrorCode.PasswordResetTwoFactorChallengesMaxAttempts
              break
          }
        }
      })
      .addCase(authCheckChallenge.fulfilled, (state) => {
        if (isNullOrUndefined(state.challenge)) {
          return
        }
        state.challenge.status = ChallengeStatus.Verified
      })
      .addCase(authResetChallenge.pending, (state) => {
        state.status = PasswordResetStatus.ChangingPassword
        state.error = undefined
      })
      .addCase(authResetChallenge.rejected, (state, action) => {
        state.status = PasswordResetStatus.Error
        state.error = PasswordResetError.Other
        if (action.meta.rejectedWithValue && isPortalApiError(action.payload)) {
          switch (action.payload?.errorCode) {
            case ErrorCode.IdenticalPassword:
              state.error = PasswordResetError.IdenticalPassword
              break
            case ErrorCode.WeakPassword:
              state.error = PasswordResetError.WeakPassword
              break
          }
        }
      })
      .addCase(authResetChallenge.fulfilled, (state) => {
        state.status = PasswordResetStatus.Complete
      })
      .addCase(checkEmailEligibility.fulfilled, (state, action) => {
        state.verificationMethods.EMAIL = !action.payload.shouldDisableEmailOption
      })
      .addCase(emailLookup.fulfilled, (state, action) => {
        state.verificationMethods.SMS = action.payload.smsVerificationEnabled
      })

      .addCase(closeCheckout, () => passwordInitialState)
      .addCase(PURGE, () => passwordInitialState),
})

export default slice.reducer
